/**
 * DeckNeedsPage - Auto-generated computed list showing cards needed across all decks
 *
 * Displays cards that are in decks but not owned in collection,
 * grouped by deck with summary statistics and "Add to List" functionality.
 */
import { useCallback, useMemo, useState } from 'react';

import { FaArrowLeft, FaChevronDown, FaChevronRight, FaListUl, FaPlus } from 'react-icons/fa';
import { IoWarningOutline } from 'react-icons/io5';
import { Link } from 'react-router';

import Button from '@/components/Button/Button';
import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import CardImage from '@/components/CardImage/CardImage';
import EmptyState from '@/components/EmptyState/EmptyState';
import Spinner from '@/components/Spinner/Spinner';
import useUserSettings from '@/features/settings/hooks/api';
import useCardIndex from '@/hooks/useCardIndex';
import { formatPrice } from '@/lib/price';
import type { DeckNeedCard } from '@/types/lists';

import { useDeckNeeds } from '@/hooks/useDeckNeeds';
import AddToListSelectModal from './components/AddToListSelectModal';
import CreateListModal from './components/CreateListModal';
import { useAddCardToList, useLists } from './hooks/api';

// ============================================================================
// Types
// ============================================================================

interface DeckGroup {
  deckId: string;
  deckName: string;
  cards: DeckNeedCard[];
  totalShortfall: number;
  totalValue: number;
}

// ============================================================================
// Sub-components
// ============================================================================

interface DeckNeedsCardRowProps {
  card: DeckNeedCard;
  onAddToList: (card: DeckNeedCard) => void;
  currency: 'USD' | 'EUR';
}

function DeckNeedsCardRow({ card, onAddToList, currency }: DeckNeedsCardRowProps) {
  const { getCardByOracleId } = useCardIndex();
  const dbCard = getCardByOracleId(card.oracle_id);

  const imageUris =
    dbCard?.image_uris?.small && dbCard?.image_uris?.normal ? { small: dbCard.image_uris.small, normal: dbCard.image_uris.normal } : null;

  const cardValue = card.cheapest_price != null ? card.cheapest_price * card.shortfall : null;

  return (
    <div className="bg-storm/20 hover:bg-storm/40 group flex items-center gap-4 rounded-lg px-3 py-2.5 transition-colors">
      {/* Card thumbnail */}
      <CardHoverPreview name={card.name} imageUris={imageUris}>
        <div className="h-14 w-10 shrink-0 overflow-hidden rounded">
          <CardImage imageUris={imageUris} name={card.name} size="small" className="h-full w-full object-cover" />
        </div>
      </CardHoverPreview>

      {/* Card info */}
      <div className="min-w-0 flex-1">
        <h4 className="text-pearl truncate font-medium">{card.name}</h4>
        <div className="text-ash mt-0.5 flex items-center gap-3 text-sm">
          <span className="text-amber-400">
            Need: <span className="font-medium">{card.shortfall}</span>
          </span>
          <span>
            Own: <span className="text-silver">{card.owned}</span>
          </span>
          {card.decks.length > 1 && (
            <span className="text-ash">
              In {card.decks.length} decks ({card.total_needed} total)
            </span>
          )}
        </div>
      </div>

      {/* Price */}
      {cardValue != null && (
        <div className="text-right">
          <span className="text-silver text-sm">{formatPrice(cardValue, currency)}</span>
          {card.shortfall > 1 && <span className="text-ash block text-xs">@ {formatPrice(card.cheapest_price ?? 0, currency)} each</span>}
        </div>
      )}

      {/* Add to list button */}
      <button
        onClick={() => onAddToList(card)}
        className="text-ash hover:text-aether hover:bg-aether/10 rounded p-2 opacity-0 transition-all group-hover:opacity-100"
        title="Add to list"
      >
        <FaListUl className="h-4 w-4" />
      </button>
    </div>
  );
}

interface DeckSectionProps {
  group: DeckGroup;
  isExpanded: boolean;
  onToggle: () => void;
  onAddToList: (card: DeckNeedCard) => void;
  currency: 'USD' | 'EUR';
}

function DeckSection({ group, isExpanded, onToggle, onAddToList, currency }: DeckSectionProps) {
  return (
    <section className="border-storm/50 overflow-hidden rounded-xl border">
      {/* Deck header - clickable */}
      <button
        onClick={onToggle}
        className="bg-storm/30 hover:bg-storm/50 flex w-full items-center gap-3 px-4 py-3 text-left transition-colors"
      >
        {isExpanded ? (
          <FaChevronDown className="text-ash h-3.5 w-3.5 shrink-0" />
        ) : (
          <FaChevronRight className="text-ash h-3.5 w-3.5 shrink-0" />
        )}

        <div className="min-w-0 flex-1">
          <Link
            to={`/decks/${group.deckId}`}
            onClick={(e) => e.stopPropagation()}
            className="text-pearl hover:text-aether truncate font-medium transition-colors"
          >
            {group.deckName}
          </Link>
          <p className="text-ash mt-0.5 text-sm">
            {group.cards.length} {group.cards.length === 1 ? 'card' : 'cards'} needed
            {group.totalShortfall > group.cards.length && ` (${group.totalShortfall} copies)`}
            {group.totalValue > 0 && <span className="text-silver"> · {formatPrice(group.totalValue, currency)}</span>}
          </p>
        </div>
      </button>

      {/* Card list */}
      {isExpanded && (
        <div className="space-y-1 p-2">
          {group.cards.map((card) => (
            <DeckNeedsCardRow key={card.oracle_id} card={card} onAddToList={onAddToList} currency={currency} />
          ))}
        </div>
      )}
    </section>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function DeckNeedsPage() {
  const { settings } = useUserSettings();
  const { cards, totalNeeded, totalValue, uniqueCardsNeeded, isLoading } = useDeckNeeds();
  const { data: lists } = useLists();
  const addCardToList = useAddCardToList();
  const { getCardByOracleId } = useCardIndex();

  // UI state
  const [expandedDecks, setExpandedDecks] = useState<Set<string>>(new Set());
  const [selectedCard, setSelectedCard] = useState<DeckNeedCard | null>(null);
  const [isAddAllModalOpen, setIsAddAllModalOpen] = useState(false);
  const [isCreateListOpen, setIsCreateListOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  // Group cards by deck
  const deckGroups = useMemo((): DeckGroup[] => {
    const groupMap = new Map<string, DeckGroup>();

    for (const card of cards) {
      for (const source of card.decks) {
        let group = groupMap.get(source.deck_id);
        if (!group) {
          group = {
            deckId: source.deck_id,
            deckName: source.deck_name,
            cards: [],
            totalShortfall: 0,
            totalValue: 0,
          };
          groupMap.set(source.deck_id, group);
        }

        // Add card if not already present (avoid duplicates)
        if (!group.cards.some((c) => c.oracle_id === card.oracle_id)) {
          group.cards.push(card);
          group.totalShortfall += card.shortfall;
          if (card.cheapest_price != null) {
            group.totalValue += card.cheapest_price * card.shortfall;
          }
        }
      }
    }

    // Sort groups by total shortfall descending
    return Array.from(groupMap.values()).sort((a, b) => b.totalShortfall - a.totalShortfall);
  }, [cards]);

  // Handlers
  const toggleDeck = useCallback((deckId: string) => {
    setExpandedDecks((prev) => {
      const next = new Set(prev);
      if (next.has(deckId)) {
        next.delete(deckId);
      } else {
        next.add(deckId);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedDecks(new Set(deckGroups.map((g) => g.deckId)));
  }, [deckGroups]);

  const collapseAll = useCallback(() => {
    setExpandedDecks(new Set());
  }, []);

  const handleAddSingleToList = useCallback((card: DeckNeedCard) => {
    setSelectedCard(card);
  }, []);

  const handleAddToListConfirm = useCallback(
    async (listId: string) => {
      if (!selectedCard) return;

      const dbCard = getCardByOracleId(selectedCard.oracle_id);

      setIsAdding(true);
      try {
        await addCardToList({
          list_id: listId,
          oracle_id: selectedCard.oracle_id,
          name: selectedCard.name,
          scryfall_id: dbCard?.id,
          quantity: selectedCard.shortfall,
          priority: 'medium',
        });
        setSelectedCard(null);
      } finally {
        setIsAdding(false);
      }
    },
    [selectedCard, addCardToList, getCardByOracleId],
  );

  const handleAddAllToList = useCallback(
    async (listId: string) => {
      setIsAdding(true);
      try {
        // Add all cards to list one by one
        for (const card of cards) {
          const dbCard = getCardByOracleId(card.oracle_id);
          await addCardToList({
            list_id: listId,
            oracle_id: card.oracle_id,
            name: card.name,
            scryfall_id: dbCard?.id,
            quantity: card.shortfall,
            priority: 'medium',
          });
        }
        setIsAddAllModalOpen(false);
      } finally {
        setIsAdding(false);
      }
    },
    [cards, addCardToList, getCardByOracleId],
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="border-b border-zinc-800 px-6 py-4">
        <div className="flex items-center gap-4">
          {/* Back link */}
          <Link to="/lists" className="text-ash hover:text-pearl transition-colors">
            <FaArrowLeft className="h-4 w-4" />
          </Link>

          {/* Icon and title */}
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/20 text-amber-400">
              <IoWarningOutline className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-zinc-100">Deck Needs</h1>
              <p className="text-sm text-zinc-500">
                {uniqueCardsNeeded > 0 ? (
                  <>
                    {uniqueCardsNeeded} unique {uniqueCardsNeeded === 1 ? 'card' : 'cards'}
                    {totalNeeded > uniqueCardsNeeded && ` · ${totalNeeded} copies total`}
                    {totalValue > 0 && <span className="text-amber-400"> · ~{formatPrice(totalValue, settings.currency)}</span>}
                  </>
                ) : (
                  'All deck cards owned!'
                )}
              </p>
            </div>
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Actions */}
          {cards.length > 0 && (
            <div className="flex items-center gap-2">
              <Button text="Expand All" variant="text" color="secondary" size="small" onClick={expandAll} />
              <Button text="Collapse All" variant="text" color="secondary" size="small" onClick={collapseAll} />
              <div className="bg-storm mx-2 h-6 w-px" />
              <Button icon={<FaPlus />} text="Add All to List" onClick={() => setIsAddAllModalOpen(true)} disabled={lists.length === 0} />
            </div>
          )}
        </div>
      </header>

      {/* Info banner */}
      <div className="border-storm/50 bg-storm/20 border-b px-6 py-3">
        <p className="text-ash text-sm">
          This page shows cards that are in your <strong className="text-silver">Brewing</strong> or{' '}
          <strong className="text-silver">Active</strong> decks but not in your collection. Add them to a list to track what you need to
          acquire.
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {cards.length === 0 ? (
          <EmptyState
            title="All cards owned!"
            description="Every card in your brewing and active decks is in your collection. Nice work!"
            icon={<span className="text-4xl">🎉</span>}
            action={
              <Link to="/lists">
                <Button text="Back to Lists" icon={<FaArrowLeft />} variant="outlined" onClick={() => {}} />
              </Link>
            }
          />
        ) : (
          <div className="space-y-4">
            {deckGroups.map((group) => (
              <DeckSection
                key={group.deckId}
                group={group}
                isExpanded={expandedDecks.has(group.deckId)}
                onToggle={() => toggleDeck(group.deckId)}
                onAddToList={handleAddSingleToList}
                currency={settings.currency}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add single card to list modal */}
      {selectedCard && (
        <AddToListSelectModal
          isOpen={!!selectedCard}
          onClose={() => setSelectedCard(null)}
          onSelect={handleAddToListConfirm}
          onCreateList={() => {
            setSelectedCard(null);
            setIsCreateListOpen(true);
          }}
          title={`Add "${selectedCard.name}" to List`}
          description={`Quantity: ${selectedCard.shortfall}`}
          isLoading={isAdding}
        />
      )}

      {/* Add all to list modal */}
      <AddToListSelectModal
        isOpen={isAddAllModalOpen}
        onClose={() => setIsAddAllModalOpen(false)}
        onSelect={handleAddAllToList}
        onCreateList={() => {
          setIsAddAllModalOpen(false);
          setIsCreateListOpen(true);
        }}
        title="Add All Deck Needs to List"
        description={`${uniqueCardsNeeded} cards (${totalNeeded} copies) · ~${formatPrice(totalValue, settings.currency)}`}
        isLoading={isAdding}
      />

      {/* Create list modal */}
      <CreateListModal isOpen={isCreateListOpen} onClose={() => setIsCreateListOpen(false)} />
    </div>
  );
}
